import { useState } from "react";
import { motion } from "framer-motion";
import { HeartHandshake, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const ACTIVITY_TYPES = [
  "Ristorante / Bar",
  "Negozio / Artigianato",
  "Professionista / Freelance",
  "Associazione / No profit",
  "Altra piccola attività",
];

export default function AidProgram() {
  const [form, setForm] = useState({ business_name: "", name: "", email: "", activity_type: ACTIVITY_TYPES[0], message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/aid-request", form);
      toast.success("Richiesta inviata! Ti ricontattiamo entro 48 ore con la proposta agevolata.");
      setForm({ business_name: "", name: "", email: "", activity_type: ACTIVITY_TYPES[0], message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-white/5 px-5 py-3.5 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-[#E05638] transition-shadow";

  return (
    <section id="agevolazioni" data-testid="aid-section" className="py-24 lg:py-32 bg-[#111111] text-white border-t border-black/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="mono-label text-[#E05638] mb-6">05 // Agevolazioni</p>
          <h2 className="font-display font-bold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl">
            Piccole attività, grandi ambizioni.
          </h2>
          <p className="mt-6 text-neutral-400 leading-relaxed max-w-md">
            Crediamo che anche la bottega di quartiere meriti un sito ben fatto. Per questo riserviamo
            alle piccole attività una <span className="text-white font-medium">consulenza con prezzo ridotto</span> sulla
            creazione del sito: raccontaci chi sei e ti facciamo una proposta su misura, senza impegno.
          </p>
          <div className="mt-10 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 max-w-md">
            <HeartHandshake size={26} className="text-[#E05638] shrink-0" />
            <p className="text-sm text-neutral-300 leading-relaxed">
              Valutiamo ogni richiesta entro 48 ore. Se la tua attività rientra nel programma, la
              creazione del sito parte da una tariffa agevolata dedicata.
            </p>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 bg-white/5 border border-white/10 rounded-2xl p-8 sm:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              data-testid="aid-form-business"
              required
              placeholder="Nome della tua attività"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className={inputCls}
            />
            <input
              data-testid="aid-form-name"
              required
              placeholder="Nome e cognome"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              data-testid="aid-form-email"
              required
              type="email"
              placeholder="La tua email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
            />
            <select
              data-testid="aid-form-activity"
              value={form.activity_type}
              onChange={(e) => setForm({ ...form, activity_type: e.target.value })}
              className={`${inputCls} appearance-none cursor-pointer [&>option]:text-neutral-900`}
            >
              {ACTIVITY_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="aid-form-message"
            required
            rows={5}
            placeholder="Raccontaci la tua attività e di cosa hai bisogno…"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="aid-form-submit-button"
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-2 rounded-full bg-[#E05638] text-white px-8 py-3.5 text-sm font-semibold hover:bg-[#C8452A] transition-colors duration-300 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#E05638] focus:ring-offset-2 focus:ring-offset-[#111111]"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Richiedi consulenza agevolata
          </button>
        </motion.form>
      </div>
    </section>
  );
}
